// components/entities/shortlistCells.tsx
//
// Shared cell renderers for the three "shortlist table" row renderers
// (PortfolioFitRenderer, LeadRenderer, UnitFitRenderer).
//
// PortfolioFitRenderer is the visual REFERENCE: a monochrome look (no
// score-based colour coding) with a horizontal per-criterion breakdown and a
// `rounded-full` score pill. These helpers reproduce that look EXACTLY so all
// three tables render the signal + score columns through the same code and are
// therefore guaranteed identical.
import React from 'react';
import clsx from 'clsx';
import { Check, Loader2, Search } from '@/components/ui/lucide-memo';
import type { SourcingCriterionResult } from '#types/types';
import type { TableColumnDef } from '../portal/Table';
import { AnimatedScore } from './scoreMotion';
import { AppTooltip } from '@/components/ui/tooltip';
import { criterionTitle } from '#lib/leasing/criterionMatch';

// Frozen per-alignment cell styles: the row is rendered once per column of every table row,
// so a literal object here would allocate on each cell of each render.
const CELL_STYLE_START: React.CSSProperties = { justifyContent: 'flex-start' };
const CELL_STYLE_CENTER: React.CSSProperties = { justifyContent: 'center' };
const CELL_STYLE_END: React.CSSProperties = { justifyContent: 'flex-end' };
const CELL_STYLE_SELECTION: React.CSSProperties = { justifyContent: 'center', padding: '0 8px' };

/** Column alignment for a shortlist row cell. Shared so the shortlist tables cannot drift apart. */
export const resolveCellStyle = (column: TableColumnDef): React.CSSProperties => {
  if (column.id === '_selection') return CELL_STYLE_SELECTION;
  if (column.id === 'actions' || column.align === 'right') return CELL_STYLE_END;
  if (column.align === 'center') return CELL_STYLE_CENTER;
  return CELL_STYLE_START;
};

/** The "evaluated N days ago" label of the shortlist rows, in the kernel language. */
export const formatRelativeTimestamp = (value: string | undefined, lang: string): string | null => {
  if (!value) return null;
  const targetDate = new Date(value);
  if (Number.isNaN(targetDate.getTime())) return null;

  const diffMs = targetDate.getTime() - Date.now();
  const absDiffMs = Math.abs(diffMs);

  const second = 1000;
  const minute = 60 * second;
  const hour = 60 * minute;
  const day = 24 * hour;
  const week = 7 * day;
  const month = 30 * day;
  const year = 365 * day;

  let unit: Intl.RelativeTimeFormatUnit = 'second';
  let divisor = second;

  if (absDiffMs >= year) { unit = 'year'; divisor = year; }
  else if (absDiffMs >= month) { unit = 'month'; divisor = month; }
  else if (absDiffMs >= week) { unit = 'week'; divisor = week; }
  else if (absDiffMs >= day) { unit = 'day'; divisor = day; }
  else if (absDiffMs >= hour) { unit = 'hour'; divisor = hour; }
  else if (absDiffMs >= minute) { unit = 'minute'; divisor = minute; }

  const delta = Math.round(diffMs / divisor);
  const locale = lang === 'fr' ? 'fr-FR' : 'en-US';
  return new Intl.RelativeTimeFormat(locale, { numeric: 'auto' }).format(delta, unit);
};

export interface RubricSignalOptions {
  /**
   * When true, the breakdown is dimmed (manually invalidated / dismissed row),
   * matching PortfolioFitRenderer's invalidated tone.
   */
  isManuallyInvalidated?: boolean;
}

/**
 * The success-state criteria breakdown, as ChatGPT's designers would draw it (owner, 2026-09-28):
 * one slim rounded meter per criterion, filled to its score, in rubric order. The letters and
 * "10/15" digits read as code; five bars read as a profile at a glance. The hover label names the
 * criterion and gives its score, in the short dark pill every hover label uses.
 */
export function renderRubricSignal(
  criteriaResults: SourcingCriterionResult[] | undefined,
  opts: RubricSignalOptions = {}
): React.ReactNode {
  const { isManuallyInvalidated = false } = opts;
  return (
    <div className={clsx('flex items-center gap-2', isManuallyInvalidated && 'opacity-40')}>
      {(criteriaResults ?? []).map((criterion, idx) => {
        const ratio = criterion.maxScore > 0 ? Math.max(0, Math.min(1, criterion.score / criterion.maxScore)) : 0;
        return (
          <AppTooltip key={criterion.criterionId || idx} content={`${criterionTitle(criterion.label)} · ${criterion.score}/${criterion.maxScore}`}>
            <div
              className="h-1.5 shrink-0 overflow-hidden rounded-full bg-white/[0.08]"
              style={BAR_STYLE}
              role="meter"
              aria-label={criterionTitle(criterion.label)}
              aria-valuemin={0}
              aria-valuemax={criterion.maxScore}
              aria-valuenow={criterion.score}
            >
              {/* The width animates, so a rubric edit still shows WHICH criterion moved. */}
              <div className="h-full rounded-full bg-white/70 transition-[width] duration-500 ease-out" style={{ width: `${ratio * 100}%` }} />
            </div>
          </AppTooltip>
        );
      })}
    </div>
  );
}

/**
 * ONE CRITERION, ONE COLUMN (owner, 2026-09-28): eleven bars in one cell was too much, so each
 * criterion is its own column, coded C1..Cn in rubric order, the three heaviest shown and the rest
 * a click away (the column picker, or the header's expand handle). The bar fills its cell.
 */
export function renderCriterionBar(criterion: SourcingCriterionResult | undefined, isManuallyInvalidated = false): React.ReactNode {
  if (!criterion) return null;
  const ratio = criterion.maxScore > 0 ? Math.max(0, Math.min(1, criterion.score / criterion.maxScore)) : 0;
  return (
    <AppTooltip content={`${criterionTitle(criterion.label)} · ${criterion.score}/${criterion.maxScore}`}>
      <div
        className={clsx('h-1.5 w-full max-w-[64px] overflow-hidden rounded-full bg-white/[0.08]', isManuallyInvalidated && 'opacity-40')}
        role="meter"
        aria-label={criterionTitle(criterion.label)}
        aria-valuemin={0}
        aria-valuemax={criterion.maxScore}
        aria-valuenow={criterion.score}
      >
        <div className="h-full rounded-full bg-white/70 transition-[width] duration-500 ease-out" style={{ width: `${ratio * 100}%` }} />
      </div>
    </AppTooltip>
  );
}

/** A criterion column's header: its code, the full name on hover. */
export const CriterionHeader = ({ code, title }: { code: string; title: string }) => (
  <AppTooltip content={title}>
    <span className="cursor-default">{code}</span>
  </AppTooltip>
);

/** One width for a criterion's bar and its name in the column header, so the two line up. */
const CRITERION_BAR_WIDTH = 60;
const BAR_STYLE = { width: CRITERION_BAR_WIDTH } as const;
const LEGEND_LABEL_STYLE = { width: CRITERION_BAR_WIDTH, fontSize: 12 } as const;

/**
 * The criteria column's header is its legend: each criterion's first word sits above its bar, the
 * full name on hover. Without it five bars say nothing about what they measure (owner, 2026-09-28).
 */
export function CriteriaLegend({ criteria, fallback }: { criteria: SourcingCriterionResult[] | null; fallback: string }) {
  if (!criteria?.length) return <>{fallback}</>;
  return (
    <span className="flex items-center gap-2">
      {criteria.map((criterion, idx) => (
        <AppTooltip key={criterion.criterionId || idx} content={criterionTitle(criterion.label)}>
          <span className="shrink-0 truncate" style={LEGEND_LABEL_STYLE}>
            {criterionTitle(criterion.label).split(/\s+/)[0]}
          </span>
        </AppTooltip>
      ))}
    </span>
  );
}

export interface FitPillOptions {
  /**
   * When true, the pill is dimmed (manually invalidated / dismissed row),
   * matching PortfolioFitRenderer's invalidated tone.
   */
  isManuallyInvalidated?: boolean;
}

/**
 * The score pill: a `rounded-full` bordered pill with the score inside.
 * Internally computes the same monochrome border/bg/text colour as
 * PortfolioFitRenderer so callers just pass the number.
 */
// FIT SCORE COLOUR SCALE (owner, 2026-09-26): red to green down a ranked column, in the Notion dark
// status palette (red, orange, yellow, the SIGNED teal-green), faded like every status pill here:
// ~15% fill, ~25% stroke, the full tone on the figure. Anchored where shortlist scores actually
// sit (45 to 75), so a column of 50s and 70s reads as a gradient, not a band of one colour.
// Shades of gray, never colour (owner, 2026-09-28): a stronger fit reads lighter.
const FIT_SCORE_STOPS: Array<[number, [number, number, number]]> = [
  [45, [110, 110, 110]],
  [75, [236, 236, 236]],
];

export const fitScoreRgb = (score: number): [number, number, number] => {
  let [fromAt, from] = FIT_SCORE_STOPS[0]!;
  if (score <= fromAt) return from;
  for (const [toAt, to] of FIT_SCORE_STOPS.slice(1)) {
    if (score <= toAt) {
      const t = (score - fromAt) / (toAt - fromAt);
      return [0, 1, 2].map((i) => Math.round(from[i]! + (to[i]! - from[i]!) * t)) as [number, number, number];
    }
    [fromAt, from] = [toAt, to];
  }
  return from;
};

export function renderFitPill(
  score: number | null | undefined,
  opts: FitPillOptions = {}
): React.ReactNode {
  const { isManuallyInvalidated = false } = opts;

  const hasScore = typeof score === 'number' && Number.isFinite(score);
  // A struck-out or unscored row stays monochrome: the colour is a judgement on a live score.
  const rgb = hasScore && !isManuallyInvalidated ? fitScoreRgb(score as number).join(', ') : null;
  const finalScoreTextClass = rgb ? '' : isManuallyInvalidated ? 'text-white/35' : 'text-white/80';

  // A figure, not a badge (owner, 2026-09-28): ChatGPT prints a number plainly. The grey still
  // lightens with the score, so the column reads as a ranking.
  return (
    <div
      className="inline-flex min-w-[2.25rem] items-center font-medium tabular-nums"
      style={rgb ? { color: `rgb(${rgb})` } : undefined}
    >
      {/* The headline number. It TWEENS to its new value rather than snapping, so a re-score
          reads as the shortlist being edited in front of the user. */}
      <AnimatedScore
        value={typeof score === 'number' && Number.isFinite(score) ? score : null}
        className={clsx('transition-colors', finalScoreTextClass)}
        style={FIT_FIGURE_STYLE}
      />
    </div>
  );
}

const FIT_FIGURE_STYLE = { fontSize: 15 } as const;

/**
 * WHAT A SHORTLIST ROW SAYS WHILE IT WORKS.
 *
 * The researcher writes one sentence per research call about what it just learned
 * (`AtomicFact.narration`), so that is what the row speaks: echoing the raw `claim` strings read as
 * a machine dumping fields rather than an analyst thinking out loud. Consecutive facts from the same
 * call share a narration, hence the dedupe. Rows written before narration existed carry none, so the
 * claim stays as the fallback, and a row that has questions but no facts yet says what it is looking
 * for instead of nothing.
 *
 * Lives here because all three shortlist renderers need it and two of them had already grown their
 * own copy, one of which had silently lost the narration branch.
 */
interface ShortlistRunningSignal {
  kind: 'FOUND' | 'SCANNING' | 'RESEARCHING';
  text: string;
}

export function useShortlistRunningSignal(params: {
  entityId: string;
  status?: string | null;
  updatedAt?: string | null;
  strategy?: Array<{ question?: string | null } | null> | null;
  rawFacts?: Array<{ claim?: string | null; narration?: string | null } | null> | null;
}): ShortlistRunningSignal | null {
  const { entityId, status, updatedAt, strategy, rawFacts } = params;
  const [tickerIndex, setTickerIndex] = React.useState(0);

  const items = React.useMemo<ShortlistRunningSignal[]>(() => {
    const seen = new Set<string>();
    const factItems = Array.isArray(rawFacts)
      ? rawFacts
          .map((entry) => {
            const spoken = typeof entry?.narration === 'string' ? entry.narration.trim() : '';
            return spoken || (typeof entry?.claim === 'string' ? entry.claim.trim() : '');
          })
          .filter((text) => {
            if (text.length === 0 || seen.has(text)) return false;
            seen.add(text);
            return true;
          })
          .map((text) => ({ kind: 'FOUND' as const, text }))
      : [];
    if (factItems.length > 0) return factItems;

    return Array.isArray(strategy)
      ? strategy
          .map((entry) => (typeof entry?.question === 'string' ? entry.question.trim() : ''))
          .filter((text) => text.length > 0)
          .map((text) => ({ kind: 'SCANNING' as const, text }))
      : [];
  }, [strategy, rawFacts]);

  React.useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- pre-existing pattern in this file; changing when this state settles is a behavioural change, not merge work
    setTickerIndex(0);
  }, [entityId, updatedAt, items.length]);

  React.useEffect(() => {
    if (status !== 'RUNNING' || items.length <= 1) return;
    const intervalId = setInterval(() => setTickerIndex((prev) => (prev + 1) % items.length), 700);
    return () => clearInterval(intervalId);
  }, [status, items.length]);

  return React.useMemo(() => {
    if (items.length === 0) return null;
    return items[tickerIndex % items.length] ?? null;
  }, [items, tickerIndex]);
}

/**
 * The RUNNING cell itself: what `useShortlistRunningSignal` returns, rendered. Shared by
 * LeadRenderer and UnitFitRenderer, whose markup for it was byte-identical.
 *
 * PortfolioFitRenderer deliberately keeps its own: it is the monochrome reference skin (white/70,
 * no SCANNING lane because PortfolioFitDTO carries no strategy), and folding it in here would mean
 * a tone option for one caller.
 */
export function renderRunningSignal(
  signal: ShortlistRunningSignal | null,
  initializingLabel: string
): React.ReactNode {
  if (signal?.kind === 'FOUND') {
    return (
      // Shades of gray and sentence case only (owner, 2026-09-27): a quiet trace, not a status board.
      <div className="w-full min-w-0 flex items-center gap-2 text-[10px] text-white/75">
        <Check size={10} className="shrink-0 animate-pulse text-white/50" />
        <span className="shrink-0 text-white/45">Found:</span>
        <span className="truncate">{signal.text}</span>
      </div>
    );
  }
  if (signal?.kind === 'SCANNING' || signal?.kind === 'RESEARCHING') {
    return (
      <div className="w-full min-w-0 flex items-center gap-2 text-[10px] text-white/55">
        <Search size={10} className="shrink-0 animate-pulse text-white/40" />
        <span className="shrink-0 text-white/35">{signal.kind === 'SCANNING' ? 'Scanning:' : 'Researching:'}</span>
        <span className="truncate">{signal.text}</span>
      </div>
    );
  }
  return (
    <div className="flex items-center gap-2 text-[10px] text-white/40">
      <Loader2 size={10} className="animate-spin" />
      <span>{initializingLabel}</span>
    </div>
  );
}
